import React from 'react';

import { formatAvailabilityForTiles } from '@kyruus/availability-tiles';
import { FormattedMessage } from '@kyruus/intl';
import { ProviderAvailability, Slot } from '@kyruus/provider-components';

import {
  AvailabilityModeBookingContent,
  BookingHeader,
  BookingSectionContainer
} from './styles';

import messages from '../../lib/messages';
import { LoadingBookingOptions } from './LoadingBookingOptions';
import ProfileContact from '../ProfileContact/profile-contact';
import { ScheduleAppointmentProps } from './types';
import { MobileAvailabilityContainer } from '../ProviderProfile/styles';
import {
  getBookingStrategy,
  getMaxAvailabilitiesToDisplay,
  getOpenSchedulingConfig,
  getShouldRenderDirectBookInDrawer,
  getShowRegulatedSpacesBanner
} from './utilities';
import { Case, Default, Switch } from '../Switch';
import { BookingStrategies } from '../../types';
import OpenSchedulingScheduleAppointment from '../OpenScheduling/OpenSchedulingScheduleAppointment';
import EstimatedWaitTimeSection from '../EstimatedWaitTime/EstimatedWaitTime';

export type ScheduleAppointmentSectionProps = ScheduleAppointmentProps & {
  showScheduleAppointmentSection: boolean;
};

export function ScheduleAppointmentSection(
  props: ScheduleAppointmentSectionProps
) {
  const bookingStrategy = getBookingStrategy(
    props.config,
    props.provider,
    props.availability
  );

  const isLoading = props.loadingBookingInfo;

  const shouldRenderDirectBookInDrawer = getShouldRenderDirectBookInDrawer(
    props.config
  );
  const showRegulatedSpacesBanner = getShowRegulatedSpacesBanner(props.config);
  const maxAvailabilitiesToDisplay = getMaxAvailabilitiesToDisplay(
    props.config
  );

  return (
    <MobileAvailabilityContainer>
      {props.showWaitTimeSection && (
        <EstimatedWaitTimeSection
          locations={props.locations}
          mlocations={props.mlocations}
          provider={props.provider}
          config={props.config}
          track={props.track}
          getWaitTimeCallbackFunction={props.getWaitTimeCallbackFunction}
          waitTimeMap={props.waitTimeMap}
        />
      )}
      {props.showScheduleAppointmentSection && (
        <BookingSectionContainer
          aria-labelledby="ProfileBookingSectionHeader"
          showBorder={true}
        >
          <BookingHeader
            id="ProfileBookingSectionHeader"
            data-testid="booking-header"
          >
            <FormattedMessage {...messages.bookingHeader} />
          </BookingHeader>
          <LoadingBookingOptions loading={isLoading} />

          {!isLoading && (
            <Switch on={bookingStrategy}>
              <Case when={BookingStrategies.Availability}>
                <AvailabilityModeBookingContent>
                  <ProviderAvailability
                    provider={props.provider}
                    config={props.config}
                    appointmentInfo={props.availability.apptInfo || {}}
                    locations={props.locations || []}
                    loggingFunction={props.track}
                    maxAvailabilitiesToDisplay={maxAvailabilitiesToDisplay}
                    onTimeSlotsClick={props.availability.handleTimeSlotsClick}
                    onViewMoreClick={props.availability.handleViewMoreClick}
                    purposeOptionsByRelationship={
                      props.purposeOptionsByRelationship
                    }
                    services={{
                      formatAvailability: (slots: Slot[]) =>
                        formatAvailabilityForTiles(
                          slots,
                          props.availTilesBookOnlineUrl,
                          false
                        ),
                      fetchAvailability: props.fetchProviderAvailability
                    }}
                    shouldRenderSlotsAsButtons={shouldRenderDirectBookInDrawer}
                    showRegulatedSpacesMessage={showRegulatedSpacesBanner}
                    bookSrc={props.bookSrc}
                    bookOnlineUrl={props.availTilesBookOnlineUrl}
                  />
                </AvailabilityModeBookingContent>
              </Case>
              <Case when={BookingStrategies.OpenSchedulingWidget}>
                <OpenSchedulingScheduleAppointment
                  config={getOpenSchedulingConfig(props.config)}
                  provider={props.provider}
                  track={props.track}
                />
              </Case>
              <Default>Not implemented.</Default>
            </Switch>
          )}

          <ProfileContact provider={props.provider} track={props.track} />
        </BookingSectionContainer>
      )}
    </MobileAvailabilityContainer>
  );
}
